import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays } from '@kraftwerk/shared';
import type { MacroTargets } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { readProfile, updateTargets } from '../../db/profile.js';
import { today } from '../../db/nutrition.js';
import { DayLog } from './DayLog.js';
import { FoodLibrary } from './FoodLibrary.js';
import { WeekPlan } from './WeekPlan.js';
import type { Language } from '../../i18n/index.js';
import { formatDay } from '../../format.js';
import { SwipePages, neighbourIn } from '../../gestures/SwipeProvider.js';
import { useIsChild } from '../../hooks/useModules.js';
import { SealedNotice } from '../SealedNotice.js';

/**
 * `supplements` öffnet seit 16.10.2026 die Bibliothek beim Bereich
 * Supplements — ein eigener Reiter ist es nicht mehr.
 */
export type NutritionView = 'day' | 'week' | 'library' | 'supplements';
type View = Exclude<NutritionView, 'supplements'>;

/**
 * Der Reiter „Ernährung".
 *
 * Der Tag ist die Startansicht — er wird täglich gebraucht, Wochenplan,
 * Bibliothek und Supplements verwalten nur gelegentlich. Abgehakt werden
 * Supplements auf „Heute" und im Tagesprotokoll; hier werden sie eingerichtet.
 */
export function NutritionScreen({
  onOpenSettings,
  initialView = 'day',
  initialDate = null,
}: {
  initialView?: NutritionView | undefined;
  /** Aus einer Mitteilung (Foto-Schätzung): dieser Tag statt heute. */
  initialDate?: string | null | undefined;
  /** Zu „Körper und Bedarf“ — dort entsteht der persönliche Vorschlag. */
  onOpenSettings?: (() => void) | undefined;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<View>(initialView === 'supplements' ? 'library' : initialView);
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [targets, setTargets] = useState<MacroTargets | null>(null);
  const [date, setDate] = useState(() => initialDate ?? today('Europe/Berlin'));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void readProfile(db).then((profile) => {
      setTimezone(profile.timezone);
      setTargets(profile.targets);
      setDate(initialDate ?? today(profile.timezone));
      setLoaded(true);
    });
  }, [initialDate]);

  const tabs: { key: View; label: string }[] = [
    { key: 'day', label: t('nutrition.tabs.day') },
    { key: 'week', label: t('nutrition.tabs.week') },
    { key: 'library', label: t('nutrition.tabs.library') },
  ];

  return (
    <div className="flex flex-col gap-3">
      <nav aria-label={t('nutrition.tabs.label')}>
        <ul className="flex gap-1 rounded-full border border-line bg-surface p-1">
          {tabs.map((tab) => (
            <li key={tab.key} className="flex-1">
              <button
                type="button"
                aria-current={view === tab.key ? 'page' : undefined}
                onClick={() => setView(tab.key)}
                className={`kw-btn kw-btn-small w-full px-1 ${
                  view === tab.key ? '' : 'border-0 bg-transparent text-ink-muted'
                }`}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Das Protokoll ist verschlüsselt (28.10.2026): Was noch nicht offen
          ist, fehlt im Tag nicht still. */}
      <SealedNotice id="nutrition-sealed" />

      {loaded && targets === null ? (
        <TargetSetup onSaved={(next) => setTargets(next)} onOpenSettings={onOpenSettings} />
      ) : null}

      {/* Wischen (24.10.2026): auf dem Inhalt die Unterreiter, auf dem Tag
          die Tage — die Nachbarseite schiebt sich daneben herein. */}
      <SwipePages
        current={view}
        neighbour={neighbourIn(tabs.map((tab) => tab.key))}
        onChange={setView}
        render={(key) =>
          key === 'day' ? (
            <>
              {/* Datum und Pfeile stehen fest — nur der Tag darunter gleitet. */}
              <DateSwitcher date={date} timezone={timezone} onChange={setDate} />
              <SwipePages
                mode="pager"
                current={date}
                neighbour={(day, dir) => addDays(day, dir)}
                onChange={setDate}
                render={(day) => (
                  <DayLog
                    date={day}
                    targets={targets}
                    language={i18n.language as Language}
                    timeZone={timezone}
                  />
                )}
              />
            </>
          ) : key === 'week' ? (
            <WeekPlan startDate={today(timezone)} />
          ) : (
            <FoodLibrary initialTab={initialView === 'supplements' ? 'supplements' : 'recipes'} />
          )
        }
      />
    </div>
  );
}

function DateSwitcher({
  date,
  timezone,
  onChange,
}: {
  date: string;
  timezone: string;
  onChange: (date: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isToday = date === today(timezone);

  return (
    <div className="flex items-center justify-between gap-2">
      <button
        type="button"
        onClick={() => onChange(addDays(date, -1))}
        aria-label={t('nutrition.day.previous')}
        className="kw-step"
      >
        ←
      </button>
      <p className="min-w-0 text-center text-sm">
        {formatDay(date, 'long', i18n.language)}
        {isToday ? <span className="text-ink-muted"> · {t('nutrition.day.today')}</span> : null}
      </p>
      <button
        type="button"
        onClick={() => onChange(addDays(date, 1))}
        aria-label={t('nutrition.day.next')}
        className="kw-step"
      >
        →
      </button>
    </div>
  );
}

/**
 * Zielwerte setzen, wenn im Profil noch keine stehen.
 *
 * Kein fester Vorschlag mehr: Die Werte aus dem Fachkonzept galten für
 * 190 cm / 85 kg — einem anderen Menschen vorgelegt, sind sie eine fremde
 * Messlatte, die wie eine eigene aussieht. Persönlich rechnet die App aus
 * „Körper und Bedarf“ (E12); wer eigene Zahlen hat, trägt sie selbst ein.
 */
function TargetSetup({
  onSaved,
  onOpenSettings,
}: {
  onSaved: (targets: MacroTargets) => void;
  onOpenSettings?: (() => void) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Ein Teen (27.10.2026) setzt seine Ziele selbst — einen persönlichen
  // Vorschlag gibt es für Jugendliche nicht.
  const child = useIsChild();
  const [manual, setManual] = useState(child);
  const [values, setValues] = useState<Record<keyof MacroTargets, string>>({
    kcal: '',
    protein: '',
    carbs: '',
    fat: '',
  });
  const [failed, setFailed] = useState(false);
  const parsed: MacroTargets = {
    kcal: Number(values.kcal.replace(',', '.')),
    protein: Number(values.protein.replace(',', '.')),
    carbs: Number(values.carbs.replace(',', '.')),
    fat: Number(values.fat.replace(',', '.')),
  };
  const complete = Object.values(parsed).every((value) => Number.isFinite(value) && value > 0);

  return (
    <section
      aria-labelledby="target-setup-heading"
      className="kw-tile flex flex-col gap-3 border-warn"
    >
      <div>
        <h3 id="target-setup-heading" className="font-medium">
          {t('nutrition.targets.heading')}
        </h3>
        <p className="mt-1 text-sm text-ink-muted">
          {t(child ? 'nutrition.targets.teenIntro' : 'nutrition.targets.intro')}
        </p>
      </div>
      {manual ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!complete) return;
            void updateTargets(parsed, db).then((ok) => {
              if (ok) onSaved(parsed);
              else setFailed(true);
            });
          }}
        >
          <div className="flex flex-wrap gap-2">
            {(['kcal', 'protein', 'carbs', 'fat'] as const).map((key) => (
              <label key={key} className="kw-label flex w-24 flex-col gap-1.5">
                {t(`nutrition.macro.${key}`)}
                <input
                  inputMode="numeric"
                  value={values[key]}
                  onChange={(event) => setValues({ ...values, [key]: event.target.value })}
                  className="kw-field"
                />
              </label>
            ))}
          </div>
          {failed ? (
            <p role="alert" className="text-xs text-warn">
              {t('nutrition.targets.noProfile')}
            </p>
          ) : null}
          <button type="submit" disabled={!complete} className="kw-btn w-full">
            {t('nutrition.targets.save')}
          </button>
        </form>
      ) : (
        <div className="flex flex-col gap-2">
          {onOpenSettings ? (
            <button type="button" onClick={onOpenSettings} className="kw-btn w-full">
              {t('nutrition.targets.personal')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setManual(true)}
            className="kw-btn kw-btn-quiet kw-btn-small w-full"
          >
            {t('nutrition.targets.manual')}
          </button>
        </div>
      )}
    </section>
  );
}
